// src/pages/AboutUs/AboutUs.tsx
import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from "react";
import { Helmet } from "react-helmet";
import { Image } from "../../components/Image/Image";
import { ROUTES } from "../../lib/constants";
import styles from "./AboutUs.module.scss";

/** assets */
import seal from "./AboutUs/Ellipse 44.png"; // yellow dot pattern (decor)
import dots from "./AboutUs/Group 158.png"; // CPUC seal
import heroWhy from "./AboutUs/Group 141.png";
import giving1 from "./AboutUs/Home - 4.png";
import giving2 from "./AboutUs/OCRiptide.jpeg";
import giving3 from "./AboutUs/CitysideExecs.jpg";
import giving4 from "./AboutUs/IrvineMayorGroundbreaking.jpg";
import giving5 from "./AboutUs/KillerDanaSurf.jpeg";
import giving6 from "./AboutUs/ElToroFootball.jpg";
import giving7 from "./AboutUs/SponsorOCRiptide.jpeg";
import giving8 from "./AboutUs/SanClementeAnimalShelter.jpg";
import giving9 from "./AboutUs/CitysideIrvine.jpg";

/** quick helper to read paths from ROUTES with fallback */
const rPath = (key: string, fallback: string) =>
  ((ROUTES as any)?.[key]?.path ??
    (ROUTES as any)?.[key] ??
    fallback) as string;

/** tiny SVG check badge that matches the comp */
const CheckDot: React.FC = () => (
  <svg width="18" height="18" viewBox="0 0 20 20" aria-hidden="true">
    <circle cx="10" cy="10" r="10" fill="#19C2F7" />
    <path
      d="M6 10.2l2.4 2.4L14 7"
      fill="none"
      stroke="#fff"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/** ===== Simple, dependency-free carousel ===== */
type Slide = { src: string; alt?: string };

const GivingBackCarousel: React.FC<{
  slides: Slide[];
  intervalMs?: number;
}> = ({ slides, intervalMs = 4000 }) => {
  const [index, setIndex] = useState(0);
  const timerRef = useRef<number | null>(null);
  const frameRef = useRef<HTMLDivElement | null>(null);
  const count = slides.length;

  const goTo = useCallback(
    (i: number) => {
      setIndex((prev) => {
        const next = (i + count) % count;
        if (next === prev) return prev;
        return next;
      });
    },
    [count]
  );

  const next = useCallback(() => goTo(index + 1), [goTo, index]);
  const prev = useCallback(() => goTo(index - 1), [goTo, index]);

  const start = useCallback(() => {
    if (timerRef.current) window.clearInterval(timerRef.current);
    timerRef.current = window.setInterval(
      next,
      intervalMs
    ) as unknown as number;
  }, [next, intervalMs]);

  const stop = useCallback(() => {
    if (timerRef.current) window.clearInterval(timerRef.current);
    timerRef.current = null;
  }, []);

  useEffect(() => {
    start();
    return () => stop();
  }, [start, stop]);

  // Pause on hover
  useEffect(() => {
    const el = frameRef.current;
    if (!el) return;
    const onEnter = () => stop();
    const onLeave = () => start();
    el.addEventListener("mouseenter", onEnter);
    el.addEventListener("mouseleave", onLeave);
    return () => {
      el.removeEventListener("mouseenter", onEnter);
      el.removeEventListener("mouseleave", onLeave);
    };
  }, [start, stop]);

  // Keyboard navigation
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "ArrowRight") next();
      if (e.key === "ArrowLeft") prev();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [next, prev]);

  // Touch / swipe
  useEffect(() => {
    const el = frameRef.current;
    if (!el) return;
    let startX = 0;
    const onTouchStart = (e: TouchEvent) => (startX = e.touches[0].clientX);
    const onTouchEnd = (e: TouchEvent) => {
      const delta = e.changedTouches[0].clientX - startX;
      if (Math.abs(delta) > 40) (delta < 0 ? next : prev)();
    };
    el.addEventListener("touchstart", onTouchStart, { passive: true });
    el.addEventListener("touchend", onTouchEnd, { passive: true });
    return () => {
      el.removeEventListener("touchstart", onTouchStart);
      el.removeEventListener("touchend", onTouchEnd);
    };
  }, [next, prev]);

  return (
    <div className={styles.givingFrame} ref={frameRef}>
      <div className={styles.givingPicture} aria-roledescription="carousel">
        <img
          src={slides[index].src}
          alt={slides[index].alt ?? "Community giving highlight"}
          loading="lazy"
        />

        {/* SCSS-styled controls */}
        <button
          type="button"
          aria-label="Previous slide"
          className={styles.navPrev}
          onClick={prev}
        >
          ‹
        </button>
        <button
          type="button"
          aria-label="Next slide"
          className={styles.navNext}
          onClick={next}
        >
          ›
        </button>
      </div>

      {/* Dots */}
      <div
        className={styles.dotsRow}
        role="tablist"
        aria-label="Carousel Pagination"
      >
        {slides.map((_, i) => (
          <button
            key={i}
            role="tab"
            aria-selected={i === index}
            aria-label={`Go to slide ${i + 1}`}
            className={`${styles.dot} ${i === index ? styles.dotActive : ""}`}
            onClick={() => goTo(i)}
          />
        ))}
      </div>
    </div>
  );
};

export const AboutUs: React.FC = () => {
  const checkAvailability = rPath("checkAvailability", "/check-availability");
  const services = rPath("services", "/services");
  const smartBiz = rPath("smartBiz", "/smartbiz");
  const construction = rPath("construction", "/construction");
  const contact = rPath("contact", "/contact");

  const orgSchema = useMemo(
    () => ({
      "@context": "https://schema.org",
      "@type": "Organization",
      name: "Cityside Fiber",
      url: "https://www.citysidefiber.com",
      logo: "https://www.citysidefiber.com/logo.png",
      sameAs: [
        "https://www.facebook.com/citysidefiber",
        "https://www.instagram.com/citysidefiber",
        "https://www.linkedin.com/company/citysidefiber",
        "https://x.com/citysidefiber",
      ],
    }),
    []
  );

  const givingSlides: Slide[] = [
    {
      src: giving1,
      alt: `Cityside Fiber supporting local community event`,
    },
    {
      src: giving2,
      alt: `Cityside Fiber at OC Ripetide Game`,
    },
    {
      src: giving3,
      alt: `Cityside Fiber executive team at construction event`,
    },
    {
      src: giving4,
      alt: `Cityside Fiber with Irvine Mayor at groundbreaking event`,
    },
    {
      src: giving5,
      alt: `Cityside Fiber at Killer Dana Surf event`,
    },
    {
      src: giving6,
      alt: `Cityside Fiber sponsoring El Toro High School Football`,
    },
    {
      src: giving7,
      alt: `Cityside Fiber reps sponsoring OC Ripetide`,
    },
    {
      src: giving8,
      alt: `Cityside Fiber supporting San Clemente Animal Shelter`,
    },
    {
      src: giving9,
      alt: `Cityside with Irvine Mayor`,
    }
  ];

  return (
    <>
      <Helmet>
        <title>{(ROUTES as any)?.aboutUs?.title ?? "About Us"}</title>
        <meta
          name="description"
          content="Cityside Fiber is SoCal’s new choice for fast, reliable fiber internet—built locally with concierge-level service."
        />
        <meta
          property="og:title"
          content={`${(ROUTES as any)?.aboutUs?.title ?? "About Us"}`}
        />
        <meta
          property="og:description"
          content="Cityside Fiber is SoCal's new choice for fast, reliable fiber internet—built locally with concierge-level service."
        />
        <meta
          property="og:url"
          content="https://www.citysidefiber.com/about-us"
        />
        <script type="application/ld+json">{JSON.stringify(orgSchema)}</script>
      </Helmet>

      {/* ===== Why Choose (hero-style) ===== */}
      <section
        className={`${styles.section} ${styles.aiWhy}`}
        aria-labelledby="why-title"
      >
        <h2 id="why-title" className={styles.whyTitle}>
          Why Choose Cityside Fiber?
        </h2>
        <div className="grid-12">
          <div className={`${styles.container} ${styles.aiGrid}`}>
            <div className={styles.aiCopy}>
              <h3 className={styles.aiSub}>
                Cityside Fiber Delivers <br /> to Our Communities
              </h3>

              <p>
                Based in Irvine, we are building a new lightning-fast 100%
                fiber-optic internet network across Southern California because
                we believe everyone deserves access to a reliable service needed
                for today's technology. In conjunction with our partner cities,
                we are installing, maintaining, and offering state of the art
                Gig-speed connections, so consumers no longer need to deal with
                the lackluster connections provided by copper cable TV providers
                (Frankly, their obsolete network and services aren't even
                comparable to Cityside Fiber!).
              </p>
              <p>
                At Cityside Fiber, we are not an out of touch big company. We
                are your neighbors. You will see us at the farmer's market, at
                Little League games, or grabbing coffee down the street. We live
                here too. We are a premier high speed internet provider,
                offering our members the best choice for online connectivity
                combined with superior concierge-level customer service,
                reliability, antivirus, and parental controls.
              </p>
              <p>
                Say goodbye to the old copper cable companies that provide
                buffering screens, dropped video calls, and internet bills full
                of add-ons. Switch to Cityside today. We look forward to you
                being one of our members!
              </p>
            </div>

            <div className={styles.aiImage}>
              <Image
                sources={[
                  { src: heroWhy, breakpoint: "default" },
                  { src: heroWhy, breakpoint: "960px" },
                ]}
                alt="Orange County neighborhood at sunset, representing Cityside Fiber communities"
              />
            </div>
          </div>
        </div>
      </section>

      {/* ===== Connected Communities (pixel-match) ===== */}
      <section
        className={`${styles.section} ${styles.connected}`}
        aria-labelledby="connected-title"
      >
        {/* right-side yellow dots only (decorative) */}
        <div aria-hidden="true" className={styles.dotsWrap}>
          <img src={dots} alt="" className={styles.rightDots} />
        </div>

        <div>
          <div className={`${styles.container} ${styles.connectedGrid}`}>
            <h2 id="connected-title" className={styles.connectedHeader}>
              Cityside Fiber Connected Communities
            </h2>

            <div className={styles.cols}>
              <div className={styles.col}>
                <div className={styles.colTitle}>Available Now</div>
                <ul className={styles.list} role="list">
                  <li>
                    <CheckDot />
                    <span>Irvine</span>
                  </li>
                  <li>
                    <CheckDot />
                    <span>Lake Forest</span>
                  </li>
                  <li>
                    <CheckDot />
                    <span>North Tustin</span>
                  </li>
                  <li>
                    <CheckDot />
                    <span>Mission Viejo</span>
                  </li>
                  <li>
                    <CheckDot />
                    <span>Dana Point</span>
                  </li>
                  <li>
                    <CheckDot />
                    <span>Tustin</span>
                  </li>
                  <li>
                    <CheckDot />
                    <span>Laguna Hills</span>
                  </li>
                  <li>
                    <CheckDot />
                    <span>San Juan Capistrano</span>
                  </li>
                </ul>
              </div>

              <div className={styles.col}>
                <div className={styles.colTitle}>Coming Soon</div>
                <ul className={styles.list} role="list">
                  <li>
                    <CheckDot />
                    <span>Rancho Santa Margarita</span>
                  </li>
                  <li>
                    <CheckDot />
                    <span>Corona</span>
                  </li>
                  <li>
                    <CheckDot />
                    <span>Santa Clarita</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* CPUC seal row INSIDE blue band */}
            <div className={styles.cpuRow}>
              <img
                src={seal}
                alt="California Public Utilities Commission Seal"
                className={styles.seal}
              />
              <p className={styles.cpuText}>
                Is Cityside Fiber a Certified California <br /> Public Utility
                Company? <strong>Yes, we are!</strong>
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ===== What Sets Us Apart ===== */}
      <section
        className={`${styles.section} ${styles.setsApart}`}
        aria-labelledby="apart-title"
      >
        <div className="grid-12">
          <div className={`${styles.container} ${styles.apartWrap}`}>
            <h2 id="apart-title" className={styles.apartHeading}>
              What Sets Us Apart
            </h2>

            <div className={styles.cards}>
              <article className={styles.card}>
                <h3>
                  Local & Connected To
                  <br />
                  Our Neighborhoods
                </h3>
                <p>
                  We live here, work here, and support our local communities
                  through various sponsorships.
                </p>
              </article>

              <article className={styles.card}>
                <h3>
                  Fiber Internet That
                  <br />
                  Delivers
                </h3>
                <p>
                  Simple. Fast. Honest. That’s our promise: real gig-speed fiber internet at prices that make sense, with zero surprises.
                </p>
              </article>

              <article className={styles.card}>
                <h3>
                  Concierge Members
                  <br />
                  That Care
                </h3>
                <p>
                  Local SoCal team, real humans, concierge-level service. No robots, no gimmicks, no long-term contract traps.
                </p>
              </article>
            </div>
          </div>
        </div>
      </section>

      {/* ===== Giving Back ===== */}
      <section
        className={`${styles.section} ${styles.giving}`}
        aria-labelledby="giving-title"
      >
        <div className="grid-12">
          <div className={styles.container}>
            <h2 id="giving-title" className={styles.givingTitle}>
              Giving Back
            </h2>

            <GivingBackCarousel slides={givingSlides} intervalMs={4500} />

            <p className={styles.givingCopy}>
              We’re not just here to connect communities with faster internet.
              From day one, giving back has been part of who we are. As we grow,
              we will keep investing in the neighborhoods that support us
              through sponsorships, donations, and good old fashioned showing
              up.
            </p>

            {/* blue thanks band */}
            <div className={styles.thanksBand}>
              <p>
                <strong>
                  Thanks for letting us be more than just your internet
                  provider.
                </strong>{" "}
                We are proud to be your neighbors and even prouder to
                deliver the internet Southern California has been waiting for.
              </p>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default AboutUs;
